import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

const { FiPhone, FiMessageCircle, FiMapPin, FiClock, FiAward, FiZap, FiShield, FiUsers } = FiIcons;

const Hero = () => {
  const { t } = useLanguage();

  const getCurrentDaySchedule = () => {
    const now = new Date();
    const day = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    if (day >= 1 && day <= 5) { // Monday to Friday
      return { day: t('hero.schedule.weekdays'), hours: t('hero.schedule.weekdaysHours') };
    } else if (day === 6) { // Saturday
      return { day: t('hero.schedule.saturday'), hours: t('hero.schedule.saturdayHours') };
    } else { // Sunday
      return { day: t('hero.schedule.sunday'), hours: t('hero.schedule.sundayHours') };
    }
  };

  const currentSchedule = getCurrentDaySchedule();

  return (
    <section className="pt-24 pb-12 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold"
              >
                <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                <span>{t('hero.subtitle')}</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight"
              >
                {t('hero.title')}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-xl text-gray-600 leading-relaxed"
              >
                {t('hero.description')}
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open('https://wa.me/34642643499', '_blank')}
                className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                <span>{t('hero.cta')}</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.location.href = 'tel:+34642643499'}
                className="flex items-center justify-center space-x-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold border-2 border-blue-600 hover:bg-blue-50 transition-all"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>{t('hero.phone')}</span>
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex items-center space-x-6 pt-4"
            >
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-gray-700">{t('hero.available')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiAward} className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">20+ {t('hero.experience')}</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Técnico informático reparando ordenador en La Manga del Mar Menor"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1 }}
              className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiUsers} className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">5000+</div>
                  <div className="text-sm text-gray-600">Clientes Satisfechos</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="grid md:grid-cols-3 gap-8 mt-20"
        >
          {[
            { icon: FiZap, key: 'fast', keyDesc: 'fastDesc', color: 'blue' },
            { icon: FiShield, key: 'warranty', keyDesc: 'warrantyDesc', color: 'green' },
            { icon: FiAward, key: 'professional', keyDesc: 'professionalDesc', color: 'purple' }
          ].map((feature, index) => (
            <motion.div
              key={feature.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 + index * 0.1 }}
              whileHover={{ y: -5 }}
              className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all"
            >
              <div className={`w-12 h-12 bg-${feature.color}-100 rounded-xl flex items-center justify-center mb-4`}>
                <SafeIcon icon={feature.icon} className={`w-6 h-6 text-${feature.color}-600`} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {t(`hero.features.${feature.key}`)}
              </h3>
              <p className="text-gray-600">
                {t(`hero.features.${feature.keyDesc}`)}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          className="mt-20 bg-white p-8 rounded-3xl shadow-xl"
        >
          <div className="text-center mb-8">
            <SafeIcon icon={FiClock} className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {t('hero.schedule.title')}
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6 }}
              className="text-center p-6 bg-blue-50 rounded-xl"
            >
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                {t('hero.schedule.weekdays')}
              </h4>
              <p className="text-xl font-bold text-blue-600">
                {t('hero.schedule.weekdaysHours')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.7 }}
              className="text-center p-6 bg-green-50 rounded-xl"
            >
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                {t('hero.schedule.saturday')}
              </h4>
              <p className="text-xl font-bold text-green-600">
                {t('hero.schedule.saturdayHours')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8 }}
              className="text-center p-6 bg-purple-50 rounded-xl"
            >
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                {t('hero.schedule.sunday')}
              </h4>
              <p className="text-xl font-bold text-purple-600">
                {t('hero.schedule.sundayHours')}
              </p>
            </motion.div>
          </div>

          <div className="text-center mt-8 pt-6 border-t border-gray-200">
            <p className="text-lg font-medium text-gray-900 mb-2">
              {t('hero.schedule.emergency')}
            </p>
            <p className="text-gray-600">
              Disponibilidad 24/7 para emergencias y soporte urgente
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;